import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import FilesView from "./FilesView/FilesView";
import { FaDownload } from "react-icons/fa";
import { findFilesByKey, getCategoryBadgeProps } from "../utils/common-utils";
import { ConditionalTooltip } from "./FilesView/FilesView";
import BadgePill from "./BadgePill";
import { getUsername, getFirstName, getLastName, getEmail } from "../keycloak";
import { trackEvent, DOWNLOAD_REPORT } from "../utils/googleAnalytics";
import { useApi } from "../context/apiContext";
import usePopup from "../hooks/usePopup";
import { transformData } from "../utils/data-transform";
import { getSasUrl } from "../services/sasService";
import {
  DOWNLOAD_INSTRUCTIONS_BLOB_PATH,
  DOWNLOAD_INSTRUCTIONS_SAS_MINUTES,
  LARGE_FILE_DRAWER_THRESHOLD_BYTES,
  sasDurationMinutesForFileSize,
} from "../utils/downloadConstants";
import keycloak from "../keycloak";
import PopupDetailsView from "./PopupDetailsView";
import AISummaryExpanded from "./AISummaryExpanded";
// Code commented out instead of removed so it doesn't fail the build.
// import AISummaryModal from './AISummaryModal';

function Popup({ isOpen, onClose, details, spatialExtent, isNested = false }) {
  const [activeTab, setActiveTab] = useState("tree");
  const [infoTab, setInfoTab] = useState("general");
  const [archiveFiles, setArchiveFiles] = useState([]);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [fileName, setFileName] = useState("");
  const [fileUrl, setFileUrl] = useState("");
  const [sizeInGB, setSizeInGB] = useState(0);
  const [instructionUrl, setInstructionUrl] = useState("");
  const [summaryFiles, setSummaryFiles] = useState([]);
  const [summaryExpanded, setSummaryExpanded] = useState(false);
  // Crossfade between the 3-column view and the expanded AI Summary view,
  const CROSSFADE_MS = 220;
  const [crossfadePhase, setCrossfadePhase] = useState("idle");
  const crossfadeTimeoutRef = useRef(null);
  const crossfadeRafRef = useRef(null);

  useEffect(
    () => () => {
      clearTimeout(crossfadeTimeoutRef.current);
      if (crossfadeRafRef.current)
        cancelAnimationFrame(crossfadeRafRef.current);
    },
    [],
  );

  const setSummaryExpandedWithCrossfade = (next) => {
    setSummaryExpanded((current) => {
      if (current === next) return current;
      clearTimeout(crossfadeTimeoutRef.current);
      if (crossfadeRafRef.current)
        cancelAnimationFrame(crossfadeRafRef.current);
      setCrossfadePhase("entering");
      crossfadeRafRef.current = requestAnimationFrame(() =>
        setCrossfadePhase("active"),
      );
      crossfadeTimeoutRef.current = setTimeout(
        () => setCrossfadePhase("idle"),
        CROSSFADE_MS,
      );
      return next;
    });
  };
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [pendingDownload, setPendingDownload] = useState(null);
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
  const [loadingBaselineModel, setLoadingBaselineModel] = useState(null);
  const [baselineModelError, setBaselineModelError] = useState(null);

  // API context and nested popup state
  const { apiType, apiClient } = useApi();
  const headers = useMemo(() => {
    return keycloak.token ? { Authorization: `Bearer ${keycloak.token}` } : {};
  }, []);
  const {
    isPopupOpen: isNestedPopupOpen,
    popupDetails: nestedPopupDetails,
    handleOpenPopup: handleOpenNestedPopup,
    handleClosePopup: handleCloseNestedPopup,
    popupLoading: nestedPopupLoading,
    spatialExtent: nestedSpatialExtent,
  } = usePopup(headers);

  // Handle baseline model click - search and open nested popup
  const handleBaselineModelClick = useCallback(
    async (baselineModelName) => {
      setLoadingBaselineModel(baselineModelName);
      setBaselineModelError(null);
      try {
        const requestData = {
          query: { key: "model_name", value: baselineModelName.trim() },
          page: 1,
          size: 10, // Get more results to filter for baseline models
          count: 0,
        };

        const response = await apiClient.postRequest(
          "/keyword/search",
          requestData,
          apiType,
          headers,
        );

        if (response.pageinfo.count > 0) {
          const transformedData = transformData(response);

          const baselineModels = transformedData.filter(
            (model) => model.model_category === "Baseline Model",
          );

          if (baselineModels.length > 0) {
            // Clear the search loader before opening nested popup
            setLoadingBaselineModel(null);
            // Open nested popup with first baseline model result
            await handleOpenNestedPopup(baselineModels[0]);
          } else {
            // Found models but none are Baseline Models
            setLoadingBaselineModel(null);
            setBaselineModelError(
              `No baseline model found with the name "${baselineModelName}". The model may not exist or may be categorized differently.`,
            );
          }
        } else {
          // No results found at all
          setLoadingBaselineModel(null);
          setBaselineModelError(
            `No model found with the name "${baselineModelName}". Please verify the model name and try again.`,
          );
        }
      } catch (error) {
        console.error("Error searching for baseline model:", error);
        setLoadingBaselineModel(null);
        setBaselineModelError(
          `An error occurred while searching for "${baselineModelName}". Please try again later.`,
        );
      }
    },
    [apiClient, apiType, headers, handleOpenNestedPopup],
  );

  // Parse comma-separated baseline models
  const parseBaselineModels = useCallback((baselineModelString) => {
    if (
      !baselineModelString ||
      baselineModelString === "None" ||
      baselineModelString.trim() === ""
    ) {
      return [];
    }
    return baselineModelString
      .split(",")
      .map((model) => model.trim())
      .filter((model) => model !== "");
  }, []);

  const memoizedFilesView = useMemo(() => {
    if (details?.files) {
      return <FilesView files={details.files} />;
    }
    return null;
  }, [details?.files]);

  // useEffect(() => {
  //   if (isSummaryModalOpen) {
  //     // Wait for next tick to trigger transition
  //     setTimeout(() => setModalOpenClass(true), 10);
  //   } else {
  //     setModalOpenClass(false);
  //   }
  // }, [isSummaryModalOpen]);

  const handleDownload = async (archive) => {
    const archiveFile = getArchiveFileString(archive);
    const archivePath = getArchivePathString(archive);
    if (!archiveFile || !archivePath) return;

    const location = archivePath.split("|");
    const fileParts = archiveFile.split("|");
    const fileName = fileParts[0];
    const sizeInBytes = Number.parseInt(fileParts[1], 10);
    const size = Number.isFinite(sizeInBytes) ? sizeInBytes / 1024 ** 3 : 0;
    const durationMinutes = sasDurationMinutesForFileSize(sizeInBytes);
    try {
      const fileUrl = await getSasUrl(location[0], {
        permissions: "r",
        durationMinutes,
      });
      if (
        Number.isFinite(sizeInBytes) &&
        sizeInBytes > LARGE_FILE_DRAWER_THRESHOLD_BYTES
      ) {
        let nextInstructionUrl = "";
        try {
          nextInstructionUrl = await getSasUrl(
            DOWNLOAD_INSTRUCTIONS_BLOB_PATH,
            {
              permissions: "r",
              durationMinutes: DOWNLOAD_INSTRUCTIONS_SAS_MINUTES,
            },
          );
        } catch (instructionErr) {
          console.warn(
            "Could not mint SAS for download instructions PDF:",
            instructionErr,
          );
        }
        setFileName(fileName);
        setFileUrl(fileUrl);
        setSizeInGB(size);
        setInstructionUrl(nextInstructionUrl);
        setIsDrawerOpen(true);
      } else {
        handleBrowserDownload(fileUrl, fileName);
      }
    } catch (err) {
      console.error("Failed to mint SAS URL for download:", err);
      alert(
        err.message ||
          "Unable to prepare download. Please sign in and try again.",
      );
    }
  };

  const handleBrowserDownload = (fileUrl, fileName) => {
    const anchor = document.createElement("a");
    anchor.target = "_parent";
    anchor.href = fileUrl;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    setIsDrawerOpen(false);
  };

  useEffect(() => {
    if (isOpen && details) {
      // console.log(details.files)
      const archFiles = findFilesByKey(details.files, "archive");
      setArchiveFiles(archFiles);
      setSummaryFiles(archFiles[0]);
    }
  }, [isOpen, details]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        if (summaryExpanded) {
          setSummaryExpandedWithCrossfade(false);
        } else {
          onClose();
        }
      }
    };

    if (isOpen) {
      globalThis.addEventListener("keydown", handleKeyDown);
    } else {
      globalThis.removeEventListener("keydown", handleKeyDown);
    }
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose, summaryExpanded]);

  // const handleOpenSummaryModal = () => {
  //   if (popupRef.current) {
  //     const rect = popupRef.current.getBoundingClientRect();
  //     setSummaryModalStyle({
  //       position: 'fixed',
  //       top: rect.top + 'px',
  //       left: rect.left + 'px',
  //       width: rect.width + 'px',
  //       height: rect.height + 'px',
  //       zIndex: 9999,
  //       background: '#fff',
  //       borderRadius: '12px',
  //       boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
  //       overflowY: 'auto',
  //       padding: '2rem',
  //     });
  //   }
  //   setIsSummaryModalOpen(true);
  // };

  if (!isOpen) return null;

  if (!details) {
    return (
      // <div className="popup-content" ref={popupRef}>
      <div className="popup-overlay">
        <div className="popup-content">
          <div className="popup-header">
            <h2>Error</h2>
            {/*
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <button
                aria-label="Show AI Summary"
                tabIndex={0}
                className="ai-summary-button"
                onClick={handleOpenSummaryModal}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ")
                    handleOpenSummaryModal();
                }}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: "6px",
                  borderRadius: "50%",
                  fontSize: "1.5rem",
                  color: "#facc15",
                  transition: "background 0.2s",
                }}
              >
                <FaRegLightbulb />
              </button>
              <button className="close-button" onClick={onClose}>
                X
              </button>
            </div>
            */}
            <button className="close-button" onClick={onClose}>
              X
            </button>
          </div>
          <div className="popup-body">
            <p>Error loading data. Please try again later.</p>
          </div>
          <div className="popup-footer">
            <button className="close-button" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  const renderField = (label, value) => {
    // Check if value is valid (not null, undefined, empty string, 'None', or whitespace-only)
    const isValidValue =
      value !== null &&
      value !== undefined &&
      value !== "" &&
      value !== "None" &&
      (typeof value === "string" ? value.trim() !== "" : true);

    return (
      <>
        <h3>{label}:</h3>
        <p style={{ wordBreak: "break-word" }}>
          {isValidValue ? (
            value
          ) : (
            <span className="black-text">
              N/A<span className="red-asterisk">*</span>
            </span>
          )}
        </p>
      </>
    );
  };

  const getArchiveFileString = (archive) => {
    if (!archive || typeof archive.file !== "string") {
      return "";
    }
    return archive.file;
  };

  const getArchivePathString = (archive) => {
    if (!archive || typeof archive.path !== "string") {
      return "";
    }
    return archive.path;
  };

  const renderArchiveFile = (archive) => {
    const archiveFile = getArchiveFileString(archive);
    const archivePath = getArchivePathString(archive);
    if (!archiveFile) return null;

    const fileParts = archiveFile.split("|");
    const fileName = fileParts[0];
    const sizeInBytes = Number.parseInt(fileParts[1], 10);
    const hasValidSize = Number.isFinite(sizeInBytes);

    // Format size appropriately
    let sizeLabel;
    if (!hasValidSize) {
      sizeLabel = "Unknown";
    } else if (sizeInBytes >= 1024 ** 3) {
      // GB
      sizeLabel = `${(sizeInBytes / 1024 ** 3).toFixed(2)} GB`;
    } else if (sizeInBytes >= 1024 ** 2) {
      // MB
      sizeLabel = `${(sizeInBytes / 1024 ** 2).toFixed(1)} MB`;
    } else {
      // KB
      sizeLabel = `${Math.round(sizeInBytes / 1024)} KB`;
    }

    return (
      <div className="archive-file-container">
        <ConditionalTooltip content={fileName}>
          <span className="archive-file-name">{fileName}</span>
        </ConditionalTooltip>
        <div className="archive-file-actions">
          <span className="archive-file-size">
            <BadgePill
              label={sizeLabel}
              backgroundColor={
                hasValidSize && sizeInBytes > 10 * 1024 ** 3
                  ? "#ff4d4f"
                  : "#52c41a"
              }
              textColor="#ffffff"
            />
          </span>
          <FaDownload
            filename={archiveFile}
            className="download-button archive-download-icon"
            onClick={() => {
              if (!archivePath) return;
              setPendingDownload({
                ...archive,
                file: archiveFile,
                path: archivePath,
              });
              setShowDisclaimer(true);
            }}
          />
        </div>
      </div>
    );
  };

  const renderFieldForParameters = (label, value) => (
    <>
      <h3>{label}:</h3>
      <p>
        {value === "None" ? (
          <span className="black-text">
            N/A<span className="red-asterisk">*</span>
          </span>
        ) : value ? (
          value
        ) : (
          <span className="black-text">
            N/A<span className="red-asterisk">*</span>
          </span>
        )}
      </p>
    </>
  );

  const categoryBadge = getCategoryBadgeProps(details?.model_category);

  return (
    <div className="popup-overlay" style={isNested ? { zIndex: 1040 } : {}}>
      {/* was: ref={popupRef} on this div, same as above */}
      <div
        className="popup-content"
        style={
          isNested
            ? {
                maxWidth: "85%",
                maxHeight: "85%",
                width: "85%",
                height: "85%",
              }
            : {}
        }
      >
        <div className="popup-header">
          <h2
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              margin: 0,
            }}
          >
            {details.title ? details.title : details.name}
            {/* Category Badge (Alternative Analysis / Baseline) */}
            {categoryBadge && (
              <span
                style={{
                  backgroundColor: categoryBadge.bg,
                  color: categoryBadge.textColor || "#fff",
                  marginLeft: "6px",
                  padding: "2px 12px",
                  borderRadius: "20px",
                  fontSize: "1rem",
                  fontWeight: "600",
                  display: "inline-block",
                  verticalAlign: "middle",
                  letterSpacing: "1px",
                }}
              >
                {categoryBadge.label}
              </span>
            )}
          </h2>
          {/* Gold header AI Summary button, replaced by the 'summary' tab.
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <button
              type="button"
              aria-label="Open AI Summary"
              className="ai-summary-button ai-summary-header-btn"
              onClick={handleOpenSummaryModal}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleOpenSummaryModal();
                }
              }}
              title="View AI-generated summary"
            >
              <FaRegLightbulb />
              <span className="ai-summary-header-btn-label">AI Summary</span>
            </button>
            <button className="close-button" onClick={onClose}>
              X
            </button>
          </div>
          */}
          <button className="close-button" onClick={onClose}>
            X
          </button>
        </div>
        <div className="popup-body">
          <div className="popup-crossfade">
            <div
              className={`popup-crossfade-panel${!summaryExpanded ? " is-visible" : ""}`}
            >
              <PopupDetailsView
                details={details}
                spatialExtent={spatialExtent}
                infoTab={infoTab}
                setInfoTab={setInfoTab}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                archiveFiles={archiveFiles}
                summaryFiles={summaryFiles}
                memoizedFilesView={memoizedFilesView}
                loadingBaselineModel={loadingBaselineModel}
                handleBaselineModelClick={handleBaselineModelClick}
                parseBaselineModels={parseBaselineModels}
                renderField={renderField}
                renderFieldForParameters={renderFieldForParameters}
                getArchiveFileString={getArchiveFileString}
                renderArchiveFile={renderArchiveFile}
                isDrawerOpen={isDrawerOpen}
                setIsDrawerOpen={setIsDrawerOpen}
                fileName={fileName}
                fileUrl={fileUrl}
                sizeInGB={sizeInGB}
                instructionUrl={instructionUrl}
                resolveInstructionUrl={() =>
                  getSasUrl(DOWNLOAD_INSTRUCTIONS_BLOB_PATH, {
                    permissions: "r",
                    durationMinutes: DOWNLOAD_INSTRUCTIONS_SAS_MINUTES,
                  })
                }
                handleBrowserDownload={handleBrowserDownload}
                onExpandSummary={() => setSummaryExpandedWithCrossfade(true)}
              />
            </div>
            {(summaryExpanded || crossfadePhase !== "idle") && (
              <div
                className={`popup-crossfade-panel${summaryExpanded && crossfadePhase !== "entering" ? " is-visible" : ""}`}
              >
                <AISummaryExpanded
                  guuid={details.tdis_identifier}
                  folder={summaryFiles}
                  onBack={() => setSummaryExpandedWithCrossfade(false)}
                />
              </div>
            )}
          </div>
        </div>
        <div className="popup-footer">
          <p>
            N/A<span className="red-asterisk">*</span> - Not Available/Not
            Applicable/Not Specified
          </p>
          <button className="close-button" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
      {showDisclaimer && (
        <div className="popup-overlay">
          <div className="popup-content" style={{ maxWidth: 600 }}>
            <div className="popup-header">
              <h2 style={{ fontSize: "1.1rem" }}>Disclaimer</h2>
            </div>
            <div
              className="popup-body"
              style={{
                maxHeight: 400,
                overflowY: "auto",
                fontSize: "0.92rem",
                lineHeight: 1.5,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "flex-start",
              }}
            >
              <p
                style={{
                  fontSize: "0.92rem",
                  width: "100%",
                  textAlign: "left",
                }}
              >
                The enclosed hydrologic and hydraulic models, final report, and
                GIS data were developed for the Texas General Land Office (GLO)
                for its Regional River Basin Flood Study project. The purpose of
                these models, final report, and associated GIS data is to better
                understand existing conditions for the GLO study. These products
                should not be used for construction or bidding purposes. For
                areas where FEMA effective FIRM maps do not include regulatory
                flood zones, or where Zone A is depicted, these maps could be
                considered best available data at the discretion of the local
                floodplain administrator. Enclosed models, reports, and GIS data
                may require additional effort to support a FEMA map update. The
                Texas General Land Office and any other relevant parties make no
                representations or warranties regarding the accuracy or
                completeness of the information provided in the enclosed
                documents or the data from which they were produced. No
                representations or warranties are made regarding the utility of
                any of the data or data representations for purposes other than
                those stated above. Not for use beyond those included in and
                specific to the scope of the RBFS project.
              </p>
              <div
                style={{
                  margin: "0.5rem 0 0.1rem 0",
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: "0.5rem",
                  }}
                >
                  <input
                    type="checkbox"
                    id="disclaimer-accept"
                    checked={disclaimerAccepted}
                    onChange={(e) => setDisclaimerAccepted(e.target.checked)}
                    style={{ marginRight: "0.5rem", width: 16, height: 16 }}
                  />
                  <label
                    htmlFor="disclaimer-accept"
                    style={{ userSelect: "none", cursor: "pointer" }}
                  >
                    I have read and accept the terms and conditions
                  </label>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: "1rem",
                    width: "100%",
                  }}
                >
                  <button
                    className="close-button"
                    style={{
                      background: "#87bcbf",
                      color: "#fff",
                      fontSize: "0.95rem",
                      minWidth: 90,
                      opacity: disclaimerAccepted ? 1 : 0.6,
                      cursor: disclaimerAccepted ? "pointer" : "not-allowed",
                    }}
                    disabled={!disclaimerAccepted}
                    onClick={() => {
                      setShowDisclaimer(false);
                      setDisclaimerAccepted(false);
                      if (pendingDownload) {
                        const pendingFile =
                          getArchiveFileString(pendingDownload);
                        const pendingPath =
                          getArchivePathString(pendingDownload);
                        if (!pendingFile || !pendingPath) {
                          setPendingDownload(null);
                          return;
                        }
                        const fileParts = pendingFile.split("|");
                        const fileName = fileParts[0];

                        // Track download event in Google Analytics
                        const userName = getUsername() || "Unknown";
                        const firstName = getFirstName() || null;
                        const lastName = getLastName() || null;
                        const email = getEmail() || null;

                        // Extract organization from email (GA4 restricts sending complete email addresses)
                        const getOrganizationFromEmail = (email) => {
                          if (!email?.includes("@")) {
                            return null;
                          }
                          return email
                            .split("@")[1]
                            .split(".")[0]
                            .toUpperCase();
                        };

                        const organization = getOrganizationFromEmail(email);
                        const rawPath = pendingPath.split("|")[0]; // Only the path, no metadata
                        const pathSegments = rawPath.split("/");
                        const prefix = pathSegments[0];
                        const region =
                          pathSegments.find((seg) =>
                            seg.toLowerCase().includes("region"),
                          ) || "";
                        const uuidRegex =
                          /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
                        const uuid =
                          pathSegments.find((seg) => uuidRegex.test(seg)) || "";
                        const downloadFileName = `${prefix}/${uuid}/${fileName}`;
                        trackEvent(DOWNLOAD_REPORT, "Download Report", {
                          user_name: userName,
                          first_name: firstName,
                          last_name: lastName,
                          organization: organization,
                          downloaded_file_name: downloadFileName,
                          region: region,
                        });

                        handleDownload({
                          ...pendingDownload,
                          file: pendingFile,
                          path: pendingPath,
                        });
                        setPendingDownload(null);
                      }
                    }}
                  >
                    Agree
                  </button>
                  <button
                    className="close-button"
                    style={{
                      background: "#ccc",
                      color: "#333",
                      fontSize: "0.95rem",
                      minWidth: 90,
                    }}
                    onClick={() => {
                      setShowDisclaimer(false);
                      setDisclaimerAccepted(false);
                      setPendingDownload(null);
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Loading indicator for baseline model search */}
      {loadingBaselineModel && (
        <div className="loading-indicator">
          <p>Loading baseline model</p>
          <div className="dots">
            <div className="dot"></div>
            <div className="dot"></div>
            <div className="dot"></div>
          </div>
        </div>
      )}
      {/* Error message for baseline model search */}
      {baselineModelError && (
        <div className="popup-overlay" style={{ zIndex: 1040 }}>
          <div
            style={{
              position: "relative",
              background: "#FFFFFF",
              borderRadius: "8px",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.18)",
              maxWidth: "500px",
              width: "90%",
              zIndex: 1041,
              padding: "24px",
              display: "flex",
              flexDirection: "column",
              gap: "20px",
            }}
          >
            <h2
              style={{
                fontSize: "1.5rem",
                fontWeight: "600",
                margin: 0,
                color: "var(--color-error, #dc3545)",
              }}
            >
              Error
            </h2>
            <p
              style={{
                color: "var(--color-text, #334755)",
                fontSize: "1rem",
                lineHeight: "1.6",
                margin: 0,
                wordBreak: "break-word",
                whiteSpace: "normal",
                display: "block",
              }}
            >
              {baselineModelError}
            </p>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <button
                onClick={() => setBaselineModelError(null)}
                style={{
                  background: "var(--color-accent, #87BCBF)",
                  color: "#FFFFFF",
                  border: "none",
                  borderRadius: "6px",
                  padding: "10px 24px",
                  fontSize: "0.95rem",
                  fontWeight: "600",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  boxShadow: "0 2px 4px rgba(135, 188, 191, 0.2)",
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = "var(--color-primary, #6E8CA0)";
                  e.target.style.boxShadow =
                    "0 4px 8px rgba(135, 188, 191, 0.3)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = "var(--color-accent, #87BCBF)";
                  e.target.style.boxShadow =
                    "0 2px 4px rgba(135, 188, 191, 0.2)";
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Nested Popup for Baseline Models */}
      {nestedPopupLoading && (
        <div className="popup-overlay" style={{ zIndex: 1040 }}>
          <div
            className="popup-content"
            style={{
              maxWidth: "85%",
              maxHeight: "85%",
              width: "85%",
              height: "85%",
              zIndex: 1041,
            }}
          >
            <div className="loading-container">
              <p className="loading-style">Loading model details...</p>
            </div>
          </div>
        </div>
      )}
      {!nestedPopupLoading && isNestedPopupOpen && nestedPopupDetails && (
        <Popup
          isOpen={isNestedPopupOpen}
          onClose={handleCloseNestedPopup}
          details={nestedPopupDetails}
          spatialExtent={nestedSpatialExtent}
          isNested={true}
        />
      )}
      {/* rect-snapshot AI Summary overlay, replaced by the
          'summary' tab + AISummaryContent.
      {isSummaryModalOpen && (
        <AISummaryModal
          guuid={details.tdis_identifier}
          isOpen={setIsSummaryModalOpen}
          onClose={() => setIsSummaryModalOpen(false)}
          folder={summaryFiles}
          summaryModalStyle={summaryModalStyle}
        />
      )}
      */}
    </div>
  );
}

Popup.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  // Large API payload — keep opaque to avoid brittle nested shapes (Sonar S6774).
  details: PropTypes.object,
  spatialExtent: PropTypes.any,
  isNested: PropTypes.bool,
};

export default Popup;
